import { buildProductAreaDeciles } from '../../../shared/utils/productAreaDeciles.js';
import { resolvePrice } from '../../../shared/utils/productUtils.js';
import { STATUS_PRIORITY } from '../../../shared/constants/availabilityStatus.js';

import { areFilterConfigsEqual } from './filterUtils.js';
import { FILTER_REGISTRY } from '../registries/filterRegistry.js';
import { getSortEntry } from '../registries/sortRegistry.js';
import { FILTER_IDS } from '../constants/filterConstants.js';
import { SORT_PARAMETER } from '../constants/sortParameters.js';
/**
 * Compare two products using the active catalog sort configuration.
 * Shared by filterProducts and ProductView navigation so both use the
 * exact same ordering semantics.
 *
 * @param {object} a
 * @param {object} b
 * @param {Array}  sortConfig
 * @param {Function} [isFavorite]
 * @returns {number}
 */
function compareProductsBySortConfig(a, b, sortConfig, context) {
	for (const level of sortConfig ?? []) {
		if (!level.parameter) continue;

		const entry = getSortEntry(level.parameter);
		if (!entry || typeof entry.compare !== 'function') continue;

		const diff = entry.compare(a, b, context);

		if (diff !== 0) {
			return level.direction === 'asc' ? diff : -diff;
		}
	}

	return 0;
}

function getProductKey(product) {
	return product?.id_cad ?? product?.uuid ?? null;
}

/**
 * Resolves previous/next products relative to the current ordered catalog list.
 *
 * `products` is expected to already be filtered and sorted by the catalog domain.
 * If the current product is the first/last item, navigation wraps around to the
 * opposite end of the list. If the current product is absent from the sequence,
 * this resolver returns no neighbors; the caller must decide how to reconcile
 * the active selection.
 *
 * @param {Array} products
 * @param {object|null} currentProduct
 * @returns {{ previousProduct: object|null, nextProduct: object|null }}
 */
export function resolveAdjacentProductsBySort(
	products,
	currentProduct,
) {
	const currentProductKey = getProductKey(currentProduct);
	if (!Array.isArray(products) || !currentProductKey) {
		return { previousProduct: null, nextProduct: null };
	}

	if (products.length <= 1) {
		return { previousProduct: null, nextProduct: null };
	}

	const currentIndex = products.findIndex(
		(product) => getProductKey(product) === currentProductKey,
	);
	if (currentIndex >= 0) {
		const previousIndex =
			currentIndex === 0 ? products.length - 1 : currentIndex - 1;
		const nextIndex =
			currentIndex === products.length - 1 ? 0 : currentIndex + 1;

		return {
			previousProduct: products[previousIndex] ?? null,
			nextProduct: products[nextIndex] ?? null,
		};
	}

	return { previousProduct: null, nextProduct: null };
}

/**
 * Filters and sorts a product list based on the active filter and sort configuration.
 *
 * Iterates over the provided filterRegistry — each entry's apply() is called only when
 * the active value differs from the default (universal skip rule). Entries with
 * apply: null (stubs) are skipped safely.
 *
 * Universal skip rule: if areFilterConfigsEqual(active, default) → skip entirely.
 * This keeps products with missing/null data visible when the user hasn't changed a filter.
 *
 * @param {Object}   params
 * @param {Array}    params.products              — displayable products (visibility === VISIBLE)
 * @param {Object}   params.filtersConfig         — current active filter values
 * @param {Object}   params.defaultFiltersConfig  — full-range defaults from buildInitialFilters
 * @param {Array}    params.sortConfig            — SortLevel[] array
 * @param {Function} [params.isFavorite]          — (uuid: string) => boolean; required for `favorites` sort
 * @returns {Array} Filtered and sorted product list
 */
export function filterProducts({
	products,
	filtersConfig,
	defaultFiltersConfig,
	sortConfig,
	filterRegistry = FILTER_REGISTRY,
	isFavorite = () => false,
	compartments = {},
}) {
	let result = [...products];

	// ── Context Preparation ───────────────────────────────────────────────────

	const outdoorList = Array.isArray(compartments)
		? compartments
		: Object.values(compartments?.compartments_types ?? compartments ?? {});

	const outdoorTypes = outdoorList
		.filter((c) => c?.group === 'OutdoorSpace')
		.map((c) => c.id);

	// lazy decile map
	const isCompareFilterActive =
		filtersConfig?.[FILTER_IDS.COMPARE_AREA] &&
		!areFilterConfigsEqual(
			filtersConfig[FILTER_IDS.COMPARE_AREA],
			defaultFiltersConfig?.[FILTER_IDS.COMPARE_AREA],
		);

	const isCompareSortActive = sortConfig?.some(
		(level) => level.parameter === SORT_PARAMETER.COMPARE_AREA,
	);

	let areaDecileMap = null;
	const getAreaDecileMap = () => {
		if (!areaDecileMap) {
			areaDecileMap = buildProductAreaDeciles(result);
		}
		return areaDecileMap;
	};

	// ── Filtering ─────────────────────────────────────────────────────────────

	for (const entry of filterRegistry) {
		const value = filtersConfig[entry.id];

		// Filter key not present in active config — nothing to do
		if (value === undefined) continue;

		// Empty array = "show all" — no filtering needed regardless of default
		if (Array.isArray(value) && value.length === 0) continue;

		// Universal skip: active value equals default → all products pass, no-op
		if (areFilterConfigsEqual(value, defaultFiltersConfig[entry.id])) continue;

		// Stub filter — apply not yet implemented
		if (typeof entry.apply !== 'function') continue;

		const filterContext = {
			outdoorTypes,
			areaDecileMap:
				entry.id === FILTER_IDS.COMPARE_AREA ? getAreaDecileMap() : null,
		};

		result = entry.apply(result, value, filterContext);
	}

	// ── Sorting ───────────────────────────────────────────────────────────────

	if (sortConfig?.length) {
		const sortContext = {
			isFavorite,
			areaDecileMap: isCompareSortActive ? getAreaDecileMap() : null,
		};

		result.sort((a, b) =>
			compareProductsBySortConfig(a, b, sortConfig, sortContext),
		);
	}

	return result;
}
